import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { KeyRound, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const INTRO_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/vqm3mfu9_gemini_generated_video_a3f175d3.mp4";

function Field({ label, children }) {
  return (
    <div>
      <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-1.5">{label}</p>
      <p className="text-foreground/80 leading-relaxed">{children}</p>
    </div>
  );
}

export function Cabinet() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const tr = (k) => t(`landing.cabinet.${k}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/cabinet/object-of-day", { params: { refresh, lang }, timeout: 60000 });
      setData(res);
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [lang]); // eslint-disable-line

  return (
    <div data-testid="cabinet">
      <div className="max-w-2xl mx-auto mb-7">
        <CinematicVideo fade noSound src={INTRO_VIDEO} testId="cabinet-cinematic-video" />
      </div>
      <div className="text-center mb-7">
        <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-4">
          <KeyRound size={14} /> {tr("eyebrow")}
        </p>
        <h3 className="font-display text-gradient-orange text-3xl sm:text-4xl leading-tight mb-3">{tr("title")}</h3>
        <p className="text-foreground/55 italic max-w-md mx-auto">{tr("subtitle")}</p>
      </div>

      {loading && !data ? (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-8">
          <Loader2 size={16} className="animate-spin" /> {tr("loading")}
        </p>
      ) : data ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={data.name}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="max-w-xl mx-auto space-y-5 text-left"
          >
            <div className="text-center">
              <p className="font-display text-gradient-orange text-3xl sm:text-4xl">{data.name}</p>
              {data.era && <p className="text-xs uppercase tracking-[0.2em] text-primary/70 mt-1">{data.era}</p>}
            </div>
            {data.story && <p className="text-foreground/80 leading-[1.85] text-center">{data.story}</p>}
            {data.wonder && <Field label={tr("wonderLabel")}>{data.wonder}</Field>}
          </motion.div>
        </AnimatePresence>
      ) : (
        <p className="text-center font-display italic text-foreground/40 py-8">{tr("error")}</p>
      )}

      <div className="text-center mt-8">
        <button
          onClick={() => load(true)}
          disabled={loading}
          data-testid="cabinet-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {tr("refresh")}
        </button>
      </div>
    </div>
  );
}
